import type { Ref } from 'vue'

/**
 * Asks before navigation, reload or tab close discards an overlay's unsaved changes.
 * Guards on the router, since overlays render outside NuxtPage. Pair with `:close-on-back="false"` on overlay-base.
 */
export default function useOverlayNavigationTrap(hasUnsavedChanges: Ref<boolean>, onNavigatedAway: () => void) {
  const widgetStore = useWidgetStore()
  const router = useRouter()

  const confirmLeave = () =>
    new Promise<boolean>(resolve => {
      widgetStore.setDialog('DialogLeavePage', {}, contextData => resolve(contextData.type === 'leave'))
    })

  const canLeave = async () => !hasUnsavedChanges.value || (await confirmLeave())

  const removeLeaveGuard = router.beforeEach(to => {
    if (to.name === 'sorry' || to.name === '404') return true

    return canLeave()
  })

  const removeLeaveHook = router.afterEach((_to, _from, failure) => {
    if (!failure) onNavigatedAway()
  })

  onScopeDispose(() => {
    removeLeaveGuard()
    removeLeaveHook()
  })

  useEventListener(window, 'beforeunload', event => {
    if (hasUnsavedChanges.value) event.preventDefault()
  })

  return {
    canLeave,
  }
}
